import axios, { type AxiosInstance } from "axios";
import {
  getPlataformaApiRoutePrefix,
  getSimulacaoBodyTokenField,
  plataformaSimulacao,
  SIMULACAO_DISTRIBUIDORAS_LIST_Q
} from "@/config/plataforma";

/**
 * Base da API (`.../api`).
 * Ordem: VITE_PLATAFORMA_BACKEND_URL → VITE_PLATAFORMA_API_BASE.
 */
export function getPlataformaApiBase(): string {
  const resolved = (import.meta.env.VITE_PLATAFORMA_BACKEND_URL || import.meta.env.VITE_PLATAFORMA_API_BASE || "")
    .trim()
    .replace(/\/$/, "");
  if (!resolved) return "";
  return resolved.endsWith("/api") ? resolved : `${resolved}/api`;
}

function createPlataformaApi(): AxiosInstance {
  return axios.create({
    baseURL: getPlataformaApiBase(),
    headers: {
      "Content-Type": "application/json"
    }
  });
}

export const plataformaApi = createPlataformaApi();

type ApiEnvelope<T> = {
  sucesso: boolean;
  entities?: T;
  msg?: unknown;
};

export type DistribuidoraOption = {
  idDistribuidora: number;
  nomeDistribuidora: string;
  situacao?: string;
};

export async function fetchDistribuidorasLandingPage(signal?: AbortSignal): Promise<DistribuidoraOption[]> {
  const base = getPlataformaApiBase();
  if (!base) {
    throw new Error("API não configurada (VITE_PLATAFORMA_API_BASE)");
  }

  const routePrefix = getPlataformaApiRoutePrefix();

  try {
    const response = await plataformaApi.get<ApiEnvelope<DistribuidoraOption[]>>(
      `/${routePrefix}/simulacao/parametro/list?q=${SIMULACAO_DISTRIBUIDORAS_LIST_Q}`,
      {
        signal,
        headers: { "Content-Type": "application/json" }
      }
    );

    const data = response.data;
    if (!data.sucesso || !data.entities) {
      throw new Error("Resposta inválida ao listar distribuidoras");
    }

    const uniq = Array.from(new Map(data.entities.map(d => [String(d.idDistribuidora), d])).values());
    return uniq;
  } catch (error) {
    console.error("Erro ao listar distribuidoras:", error);
    throw error;
  }
}

/** Campos retornados em `entities` pelo POST de simulação de lead. */
export type SimulacaoLeadEntities = {
  mes?: number;
  meses12?: number;
  plano?: number;
  situacao?: string;
  mensagens?: string | null;
  hash?: string;
  preAuthToken?: string;
  idLead?: number;
};

export type SimularEconomiaParams = {
  idDistribuidora: string;
  valorConsumo: number;
  telefoneDigits: string;
  cpfDigits: string | null;
  cnpjDigits: string | null;
  /** Valor vindo da query string (nome do parâmetro na URL é definido pelo negócio). */
  tokenFromUrl?: string;
  hasAgente?: boolean;
};

export async function simularEconomia(
  params: SimularEconomiaParams,
  signal?: AbortSignal
): Promise<SimulacaoLeadEntities> {
  const base = getPlataformaApiBase();
  if (!base) {
    throw new Error("API não configurada (VITE_PLATAFORMA_API_BASE)");
  }

  const sim = plataformaSimulacao;
  const hasAgente = params.hasAgente === true;
  const tipolead = params.cnpjDigits && params.cnpjDigits.length === 14 ? "J" : "F";
  const routePrefix = getPlataformaApiRoutePrefix();
  const tokenField = getSimulacaoBodyTokenField();

  const body: Record<string, unknown> = {
    ...sim,
    tipoCanal: hasAgente ? sim.tipoCanalVendedor : sim.tipoCanal,
    cpf: params.cpfDigits,
    cnpj: params.cnpjDigits,
    idDistribuidora: params.idDistribuidora,
    valorConsumo: params.valorConsumo,
    origem: 6,
    telefone: params.telefoneDigits,
    dispararSms: sim.dispararSms,
    tipolead,
    email: ""
  };
  body[tokenField] = params.tokenFromUrl ?? "";

  try {
    const response = await plataformaApi.post<ApiEnvelope<SimulacaoLeadEntities>>(
      `/${routePrefix}/simulacao/landingpage/lead/obter`,
      body,
      {
        signal,
        headers: { "Content-Type": "application/json" }
      }
    );

    const data = response.data;
    if (!data.sucesso) {
      throw new Error("Não foi possível calcular a simulação. Tente novamente.");
    }
    return data.entities ?? {};
  } catch (error) {
    console.error("Erro ao simular economia:", error);
    throw error;
  }
}

export async function sendLeadStatusStep(hash: string, situacao: string, signal?: AbortSignal): Promise<void> {
  const base = getPlataformaApiBase();
  if (!base) {
    throw new Error("API não configurada (VITE_PLATAFORMA_API_BASE)");
  }
  const routePrefix = getPlataformaApiRoutePrefix();

  await plataformaApi.post(
    `/${routePrefix}/lead/status`,
    { hash, situacao },
    { signal, headers: { "Content-Type": "application/json" } }
  );
}
